import { useEffect, useState } from "react";
import {
  Cross,
  Sun,
  ChatsCircle,
  HandsPraying,
  Compass,
  Plant,
  Star,
  Quotes,
  InstagramLogo,
  FacebookLogo,
  YoutubeLogo,
  Envelope,
  ArrowRight,
} from "@phosphor-icons/react";
import { Header } from "../components/Header";
import { LogoMark } from "../components/LogoMark";
import { WaitlistForm } from "../components/WaitlistForm";
import { OliveBranch, Vine } from "../components/Botanical";
import { useReveal } from "../hooks/useReveal";

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/a866d1bd-8a57-4492-b529-2e387bb68635/images/4a492e5bb013acdd262d4f5644e7124421fe8a263b200f7db8effb2e85fdf494.png",
  about:
    "https://images.unsplash.com/photo-1624646803808-9c5a9de7aa3f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  scripture:
    "https://images.unsplash.com/photo-1645620549807-0aea79c18482?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const FEATURES = [
  { icon: Cross, title: "Faith-Based Guidance", body: "Grow together on a foundation of Scripture, grace, and Christ-centered wisdom for every season." },
  { icon: Sun, title: "Daily Marriage Encouragement", body: "A gentle word each morning to help you turn toward one another with intention and warmth." },
  { icon: ChatsCircle, title: "Meaningful Conversations", body: "Thoughtful prompts that open the heart and deepen the way you truly know each other." },
  { icon: HandsPraying, title: "Couples Prayer", body: "Pray as one. Simple guided moments that invite God into the center of your marriage." },
  { icon: Compass, title: "Mentorship & Wisdom", body: "Draw from couples who have walked before you, sharing counsel rooted in lasting love." },
  { icon: Plant, title: "Marriage Growth Journey", body: "Intentional habits and milestones that help your covenant flourish year after year." },
];

const Divider = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-3 ${className}`}>
    <span className="h-px w-10 bg-gold/50" />
    <span className="h-1.5 w-1.5 rotate-45 bg-gold/70" />
    <span className="h-px w-10 bg-gold/50" />
  </span>
);

const Overline = ({ children, className = "" }) => (
  <p className={`text-xs md:text-sm uppercase tracking-[0.28em] text-gold font-medium ${className}`}>
    {children}
  </p>
);

export default function Landing() {
  useReveal();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        raf = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div id="top" className="relative overflow-x-hidden bg-evergreen">
      <div className="paper-grain" />
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative pt-36 pb-24 md:pt-44 md:pb-32">
        <OliveBranch className="pointer-events-none absolute -left-10 top-24 h-72 w-44 text-sage/25 animate-float-slow" />
        <OliveBranch className="pointer-events-none absolute right-4 bottom-8 h-64 w-40 text-sage/20 rotate-180 animate-float-slower hidden md:block" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          <div className="reveal">
            <Overline>A Luxury Faith-Based Mentoring Experience</Overline>
            <h1 className="mt-7 font-serif font-light leading-[1.02] tracking-tight text-ivory text-5xl md:text-6xl lg:text-7xl">
              Strong marriages<br />are <em className="not-italic text-gold">cultivated.</em>
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg font-light leading-relaxed text-champagne/80">
              GodTended helps Christian couples grow closer to God and to each other
              through daily encouragement, meaningful conversations, prayer, mentoring,
              and intentional habits.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#waitlist"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-medium tracking-wide text-evergreen shadow-[0_10px_30px_rgba(201,161,93,0.35)] transition-all duration-500 ease-lux hover:bg-champagne hover:-translate-y-1"
              >
                Join the Waiting List
                <ArrowRight weight="light" className="h-5 w-5 transition-transform duration-500 ease-lux group-hover:translate-x-1" />
              </a>
              <a
                href="#about"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center rounded-full border border-gold/60 px-8 py-4 font-medium text-ivory transition-all duration-500 ease-lux hover:bg-gold/10 hover:-translate-y-1"
              >
                Learn More
              </a>
            </div>
          </div>

          <div className="reveal relative">
            <div className="relative overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(0,0,0,0.4)] ring-1 ring-gold/25">
              <img
                src={IMG.hero}
                alt="A married couple embracing during golden hour in a garden"
                className="h-[440px] md:h-[560px] w-full object-cover will-change-transform"
                style={{ transform: `translateY(${scrollY * 0.06}px) scale(1.08)` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-evergreen/50 to-transparent" />
            </div>

            <div
              data-testid="hero-social-proof"
              className="absolute -bottom-8 -left-6 md:-left-10 backdrop-blur-xl bg-forest/70 border border-gold/25 rounded-3xl px-7 py-5 shadow-[0_16px_48px_rgba(0,0,0,0.35)]"
            >
              <div className="flex gap-1 text-gold">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} weight="fill" className="h-4 w-4" />
                ))}
              </div>
              <p className="mt-2 text-sm font-light text-champagne max-w-[15rem]">
                Join hundreds of couples preparing to strengthen their marriage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WAITLIST SIGNUP ===================== */}
      <section id="waitlist" className="relative py-24 md:py-32 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <Overline>Notify Me First</Overline>
            <h2 className="mt-6 font-serif font-light text-3xl md:text-4xl lg:text-5xl text-ivory leading-snug">
              Be among the first couples invited.
            </h2>
            <p className="mt-6 max-w-lg text-base md:text-lg font-light leading-relaxed text-champagne/75">
              We are opening the doors slowly and intentionally. Add your name and be the
              first to know when GodTended welcomes new couples.
            </p>
            <Divider className="mt-10" />
          </div>
          <div className="reveal lg:justify-self-end w-full">
            <div className="backdrop-blur-sm bg-forest/60 border border-gold/25 rounded-3xl p-8 md:p-10 shadow-[0_16px_48px_rgba(0,0,0,0.3)]">
              <WaitlistForm variant="light" idPrefix="waitlist" />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FEATURES ===================== */}
      <section id="features" className="relative py-24 md:py-32 scroll-mt-24 bg-forest">
        <Vine className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-4 h-16 w-80 text-sage/25" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="reveal text-center max-w-2xl mx-auto">
            <Overline>The GodTended Experience</Overline>
            <h2 className="mt-6 font-serif font-light text-3xl md:text-4xl lg:text-5xl text-ivory leading-snug">
              Everything a marriage needs to flourish.
            </h2>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="reveal group rounded-3xl bg-evergreen/50 backdrop-blur-sm border border-gold/20 p-8 md:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.2)] transition-all duration-500 ease-lux hover:-translate-y-2 hover:border-gold/40 hover:shadow-[0_20px_50px_rgba(0,0,0,0.35)]"
                style={{ transitionDelay: `${(i % 3) * 90}ms` }}
              >
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-gold/40 bg-gold/5 transition-colors duration-500 group-hover:bg-gold/15">
                  <f.icon weight="thin" className="h-7 w-7 text-gold" />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-ivory">{f.title}</h3>
                <p className="mt-3 text-base font-light leading-relaxed text-champagne/70">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== ABOUT ===================== */}
      <section id="about" className="relative py-24 md:py-32 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div className="reveal relative order-2 lg:order-1">
            <div className="overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(0,0,0,0.4)] ring-1 ring-gold/25">
              <img
                src={IMG.about}
                alt="A couple walking hand in hand at golden hour"
                className="h-[420px] md:h-[540px] w-full object-cover"
              />
            </div>
            <OliveBranch className="pointer-events-none absolute -bottom-10 -right-6 h-52 w-32 text-sage/25 rotate-12 animate-float-slow" />
          </div>

          <div className="reveal order-1 lg:order-2">
            <Overline>Our Belief</Overline>
            <h2 className="mt-6 font-serif font-light text-3xl md:text-4xl lg:text-5xl text-ivory leading-[1.15]">
              Marriage isn't finished at&nbsp;<em className="not-italic text-gold">"I do."</em>
            </h2>
            <p className="mt-8 max-w-xl text-lg font-light leading-relaxed text-champagne/80">
              Every healthy marriage requires intentional care. GodTended was created to
              help couples cultivate stronger relationships rooted in faith, grace,
              communication, prayer, and love.
            </p>
            <Divider className="mt-10" />
          </div>
        </div>
      </section>

      {/* ===================== SCRIPTURE ===================== */}
      <section id="scripture" className="relative py-28 md:py-40 scroll-mt-24">
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <div className="reveal relative overflow-hidden rounded-[2.5rem] bg-forest px-8 py-20 md:px-20 md:py-28 text-center border border-gold/20 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
            <img
              src={IMG.scripture}
              alt=""
              className="absolute inset-0 h-full w-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-forest/70" />
            <div className="relative">
              <Quotes weight="fill" className="mx-auto h-10 w-10 text-gold/70" />
              <blockquote className="mt-8 font-serif font-light italic text-ivory text-3xl md:text-5xl leading-snug">
                "A cord of three strands is not quickly broken."
              </blockquote>
              <Divider className="mt-10 justify-center" />
              <p className="mt-6 text-sm uppercase tracking-[0.28em] text-champagne/80">
                Ecclesiastes 4:12
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section id="testimonials" className="relative py-24 md:py-32 scroll-mt-24 bg-forest">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="reveal text-center max-w-2xl mx-auto">
            <Overline>Voices — Coming Soon</Overline>
            <h2 className="mt-6 font-serif font-light text-3xl md:text-4xl lg:text-5xl text-ivory leading-snug">
              Stories of couples growing in grace.
            </h2>
          </div>

          <div className="mt-16 grid md:grid-cols-3 gap-6 md:gap-8">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                data-testid={`testimonial-placeholder-${i}`}
                className="reveal rounded-3xl bg-evergreen/50 backdrop-blur-sm border border-gold/20 p-8 md:p-10 shadow-[0_8px_30px_rgba(0,0,0,0.2)]"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <div className="flex gap-1 text-gold/40">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} weight="fill" className="h-4 w-4" />
                  ))}
                </div>
                <div className="mt-6 space-y-3">
                  <span className="block h-3 w-full rounded-full bg-champagne/15" />
                  <span className="block h-3 w-11/12 rounded-full bg-champagne/15" />
                  <span className="block h-3 w-3/4 rounded-full bg-champagne/15" />
                </div>
                <div className="mt-8 flex items-center gap-3">
                  <span className="h-11 w-11 rounded-full bg-champagne/20 border border-gold/20" />
                  <div className="space-y-2">
                    <span className="block h-2.5 w-24 rounded-full bg-champagne/20" />
                    <span className="block h-2 w-16 rounded-full bg-champagne/15" />
                  </div>
                </div>
                <p className="mt-6 text-xs uppercase tracking-[0.24em] text-gold/70">
                  Coming Soon
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== COMING SOON CTA ===================== */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="reveal relative overflow-hidden rounded-[2.5rem] bg-forest border border-gold/20 px-8 py-20 md:px-20 md:py-24 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
            <OliveBranch className="pointer-events-none absolute -right-6 -top-8 h-64 w-40 text-champagne/10 rotate-180" />
            <div className="relative max-w-2xl">
              <Overline className="text-champagne">Coming Soon</Overline>
              <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl text-ivory leading-tight">
                Cultivate a marriage that lasts a lifetime.
              </h2>
              <p className="mt-6 text-lg font-light leading-relaxed text-champagne/80">
                Join the waiting list to receive early access when GodTended opens its
                doors to new couples.
              </p>
              <div className="mt-12">
                <WaitlistForm variant="dark" idPrefix="cta" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FOOTER ===================== */}
      <footer className="relative bg-evergreen text-ivory pt-24 pb-12 border-t border-gold/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-3">
                <LogoMark className="h-12 w-12" ring />
                <span className="font-serif text-2xl text-ivory">GodTended</span>
              </div>
              <p className="mt-5 max-w-sm text-sm font-light leading-relaxed text-champagne/70">
                A luxury faith-based mentoring experience helping Christian couples grow
                closer to God and to each other.
              </p>
              <p className="mt-6 text-xs uppercase tracking-[0.28em] text-gold/80">
                Faith. Love. Forever.
              </p>
            </div>

            <div className="md:justify-self-end">
              <nav className="flex flex-wrap gap-x-10 gap-y-3">
                {["About", "Privacy", "Terms", "Contact"].map((l) => (
                  <a
                    key={l}
                    href={l === "About" ? "#about" : "#waitlist"}
                    data-testid={`footer-link-${l.toLowerCase()}`}
                    className="text-sm font-light text-champagne/80 transition-colors duration-300 hover:text-gold"
                  >
                    {l}
                  </a>
                ))}
              </nav>
              <div className="mt-8 flex gap-4">
                {[InstagramLogo, FacebookLogo, YoutubeLogo, Envelope].map((Icon, i) => (
                  <a
                    key={i}
                    href="#waitlist"
                    aria-label="social link"
                    data-testid={`footer-social-${i}`}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold transition-all duration-500 ease-lux hover:bg-gold/10 hover:-translate-y-1"
                  >
                    <Icon weight="thin" className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-16 border-t border-gold/15 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs font-light text-champagne/50">
              © {new Date().getFullYear()} GodTended. All rights reserved.
            </p>
            <p className="text-xs font-light text-champagne/50">
              Cultivated with faith and love.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
